import { ref } from 'vue';

// One state shared by early service-worker events and the UI mounted afterwards.
export const availableUpdate = ref(null);
export const updateInProgress = ref(false);
export const updateStage = ref('');
export const updateError = ref('');
export const updatePreparing = ref(false);
export const updateReady = ref(false);
let accepting = false;
let reloaded = false;
let preparation;
let preparedBuild = '';
let retryTimer;
const UPDATE_TIMEOUT = 45000;
const APP_BUILD = process.env.VUE_APP_BUILD_STAMP || '';

// The query bypasses the old HTML response and is removed after the new app opens.
const initialUrl = new URL(window.location.href);
if (initialUrl.searchParams.has('_app_update')) {
  initialUrl.searchParams.delete('_app_update');
  window.history.replaceState(window.history.state, '', initialUrl.toString());
}

export function offerUpdate(detail = {}) {
  if (detail.signature === APP_BUILD) return;
  availableUpdate.value = { ...availableUpdate.value, ...detail };
  window.dispatchEvent?.(new CustomEvent('pwa:update-available', { detail }));
  if (detail.signature && preparedBuild !== detail.signature) updateReady.value = false;
  void prepareAvailableUpdate();
}

export function handleControllerChange() {
  // Another tab may activate a worker. Never discard this tab's unsaved work.
  if (!accepting) offerUpdate({ controlled: true });
}

function reloadOnce(build) {
  if (reloaded) return;
  reloaded = true;
  const target = new URL(window.location.href);
  target.searchParams.set('_app_update', `${build}-${Date.now()}`);
  window.location.replace(target.toString());
}

function withTimeout(promise) {
  let timer;
  return Promise.race([promise, new Promise((resolve, reject) => {
    timer = window.setTimeout(() => reject(new Error('Update timed out')), UPDATE_TIMEOUT);
  })]).finally(() => window.clearTimeout(timer));
}

function waitForWorker(worker, ready) {
  return new Promise((resolve, reject) => {
    const finish = (error) => {
      worker.removeEventListener('statechange', check);
      navigator.serviceWorker.removeEventListener('controllerchange', check);
      window.clearTimeout(timeout);
      window.clearInterval(poll);
      if (error) reject(error); else resolve();
    };
    const check = () => {
      if (worker.state === 'redundant') finish(new Error('Worker installation failed'));
      else if (ready()) finish();
    };
    const timeout = window.setTimeout(() => finish(new Error('Worker activation timed out')), UPDATE_TIMEOUT);
    // Covers browsers that omit controllerchange when resuming an installed app.
    const poll = window.setInterval(check, 200);
    worker.addEventListener('statechange', check);
    navigator.serviceWorker.addEventListener('controllerchange', check);
    check();
  });
}

async function activateLatestWorker() {
  if (!navigator.serviceWorker) return null;
  const base = new URL(process.env.BASE_URL || '/', window.location.origin);
  let registration = availableUpdate.value?.registration;
  if (!registration) {
    try {
      registration = await withTimeout(navigator.serviceWorker.register(new URL('service-worker.js', base).href, {
        scope: base.pathname, updateViaCache: 'none',
      }));
    } catch (error) {
      // A normal browser can block workers while still allowing the web app.
      // Only fall back when no existing worker can intercept the new release.
      if (!navigator.serviceWorker.controller) return null;
      throw error;
    }
  }
  if (!registration.waiting && !registration.installing) await withTimeout(registration.update());
  const installing = registration.installing;
  if (installing) await waitForWorker(installing, () => ['installed', 'activated'].includes(installing.state));
  const worker = registration.waiting || registration.active;
  if (!worker) throw new Error('No worker available');
  if (worker.state !== 'activated') worker.postMessage({ type: 'SKIP_WAITING' });
  await waitForWorker(worker, () => worker.state === 'activated' && navigator.serviceWorker.controller === worker);
  return worker;
}

async function clearBrowserAppCaches() {
  if (!window.caches) return;
  const scope = new URL(process.env.BASE_URL || '/', window.location.origin).href;
  try {
    const keys = await window.caches.keys();
    await Promise.all(keys.filter(key => key.startsWith('erp-versioned-assets-') ||
      (key.startsWith('workbox-') && key.endsWith(scope))).map(key => window.caches.delete(key)));
  } catch (error) { /* Storage can be disabled; fresh HTTP downloads still work. */ }
}

function prepareWorker(worker) {
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const finish = (error, build) => {
      window.clearTimeout(timeout);
      channel.port1.close(); channel.port2.close();
      if (error) reject(error); else resolve(build);
    };
    const timeout = window.setTimeout(() => finish(new Error('Worker did not acknowledge update')), UPDATE_TIMEOUT);
    channel.port1.onmessage = ({ data }) => {
      if (data?.type !== 'UPDATE_READY') return;
      finish(data.ok ? null : new Error('Cache cleanup failed'), data.build);
    };
    try { worker.postMessage({ type: 'PREPARE_UPDATE' }, [channel.port2]); }
    catch (error) { finish(error); }
  });
}

async function downloadAppShell(workerBuild) {
  const base = new URL(process.env.BASE_URL || '/', window.location.origin);
  const url = new URL('index.html', base);
  url.searchParams.set('_fresh', `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const response = await withTimeout(fetch(url.href, { cache: 'no-store' }));
  if (!response.ok) throw new Error('Application download failed');
  const html = new DOMParser().parseFromString(await response.text(), 'text/html');
  const build = html.querySelector('meta[name="app-build"]')?.content;
  if (!build || (workerBuild && workerBuild !== build)) throw new Error('Release is not ready');
  const assets = [...new Set(Array.from(html.querySelectorAll('script[src],link[rel="stylesheet"][href]'))
    .map(node => new URL(node.getAttribute('src') || node.getAttribute('href'), base))
    .filter(asset => asset.origin === base.origin).map(asset => asset.href))];
  if (!assets.some(asset => new URL(asset).pathname.endsWith('.js'))) throw new Error('Missing application scripts');
  for (let index = 0; index < assets.length; index += 4) {
    await withTimeout(Promise.all(assets.slice(index, index + 4).map(async asset => {
      // Refresh both the HTTP cache and the versioned worker cache before navigating.
      const file = await fetch(asset, { cache: 'reload' });
      if (!file.ok || file.redirected || /text\/html/i.test(file.headers.get('Content-Type') || '')) {
        throw new Error('Incomplete application release');
      }
      await file.arrayBuffer();
    })));
  }
  return build;
}

export function prepareAvailableUpdate() {
  if (preparation) return preparation;
  if (updateReady.value || accepting || navigator.onLine === false) return Promise.resolve();
  window.clearTimeout(retryTimer);
  updatePreparing.value = true;
  updateError.value = '';
  preparation = (async () => {
    try {
      const worker = await activateLatestWorker();
      const workerBuild = worker ? await prepareWorker(worker) : null;
      if (!worker) await clearBrowserAppCaches();
      const build = await downloadAppShell(workerBuild);
      const expected = availableUpdate.value?.signature;
      if (expected && expected !== build) throw new Error('A newer release is still being prepared');
      preparedBuild = build;
      updateReady.value = true;
    } catch (error) {
      updateReady.value = false;
      updateError.value = 'La nueva versión aún se está preparando. Puedes seguir trabajando.';
      retryTimer = window.setTimeout(() => { void prepareAvailableUpdate(); }, 15000);
    } finally {
      updatePreparing.value = false;
      preparation = undefined;
    }
  })();
  return preparation;
}

export function applyAvailableUpdate() {
  if (accepting) return;
  if (!updateReady.value) {
    void prepareAvailableUpdate();
    return;
  }
  // All worker/network/cache work is finished before the button is enabled.
  // A click only navigates once; it never starts another check or download.
  accepting = true;
  updateInProgress.value = true;
  updateStage.value = 'Abriendo la versión actualizada…';
  reloadOnce(preparedBuild);
}
